import { useState, useCallback, useMemo, useEffect } from "react";
import { IStatelessFormService, StatelessFormService } from "../../../entities/stateless-form-service";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { ValidatedFormValueService, IFormValidityCheck } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged } from "../../../framework/utils";
import { none, where } from "../../../framework/utils/collection";
import { ProjectEvents } from "../events";
import { ICancellationEditorFormModel, ICancellationEditorValueModel, ICancellationEditorModelChange } from "./cancellation-editor-iface";
import { RecordAssessmentRequest } from "../../../entities-generated/models/project/record-assessment-request";

// ##BEGIN:DEVELOPERCODE##

// this is a good place for doing things like defining custom form value service types

// ##END:DEVELOPERCODE##

// %%BEGIN:rationale/validated/string/string%%
export const rationale = new (class rationaleService extends ValidatedFormValueService<IStatelessFormService, string, string> {
    public initialize(data: IStatelessFormService): void {
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStatelessFormService): void {}

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter reason for this action');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'rationale' // id
    ,'Reason' // description
);
// %%END:rationale/validated/string/string%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): ICancellationEditorFormModel => {
    return {
       rationale: rationale.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): ICancellationEditorValueModel => {
    return {
       rationale: rationale.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        rationale.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: ICancellationEditorModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(ProjectEvents.CancellationModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<ICancellationEditorFormModel>> => {
    const promises = [
        rationale.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:ICancellationEditorFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: ICancellationEditorValueModel, dataModel: IStatelessFormService } = {
    dataModel: new StatelessFormService()
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: IStatelessFormService) => {
    const dataModel = data || new StatelessFormService();

    rationale.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:ICancellationEditorFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
rationale.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new StatelessFormService();

    rationale.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: ICancellationEditorModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], ProjectEvents.CancellationModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): ICancellationEditorFormModel => {
    const [getter, setter] = useState<ICancellationEditorFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: ICancellationEditorModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: ICancellationEditorFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('CancellationEditorFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('CancellationEditorFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toCancelRequest = () => {
    const v = toValueModel();
    const request = new RecordAssessmentRequest();
    request.assessmentCancelled = true;
    request.notes = v.rationale;
    return request;
}

// ##END:DEVELOPERCODE##
